import { useState, useRef } from 'react';
import { IconUpload, Text4, Text5, DotsLoader } from '@olxeu-jobs/ui';
import { FormattedMessage } from '~i18n';
import type { MimeType } from '~types';
import { formatMimeType } from '~utils/file';
import { useMedia } from '../../hooks';
import { IconWrapper, ContentWrapper, Button, Input, LoaderWrapper } from './styled-components';

type InputProps = Omit<
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'accept' | 'type' | 'children' | 'onDrop'
>;

type Props = InputProps & {
  minWidth?: number;
  minHeight?: number;
  onDrop?: React.DragEventHandler<HTMLElement>;
  loading?: boolean;
  mimeTypes?: MimeType[] | readonly MimeType[];
};

export const ImagePicker: React.FC<Props> = ({
  minWidth = 0,
  minHeight = 0,
  onDrop,
  onChange,
  className,
  loading,
  mimeTypes,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const isTouch = useMedia('touch');
  const accept = mimeTypes?.join(',');

  const handleOnClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();

    inputRef.current?.click();
  };

  const handleOnDrop: React.DragEventHandler<HTMLElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onDrop?.(event);
  };

  const handleOnDragOver: React.DragEventHandler<HTMLElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleOnDragEnter: React.DragEventHandler<HTMLElement> = () => {
    setIsDragging(true);
  };

  const handleOnDragLeave: React.DragEventHandler<HTMLElement> = () => {
    setIsDragging(false);
  };

  return (
    <>
      <Button
        $isDragging={isDragging}
        onDrop={!isTouch ? handleOnDrop : undefined}
        onDragEnter={!isTouch ? handleOnDragEnter : undefined}
        onDragOver={!isTouch ? handleOnDragOver : undefined}
        onDragLeave={!isTouch ? handleOnDragLeave : undefined}
        onClick={handleOnClick}
        className={className}
        data-testid="image-picker-trigger"
      >
        {loading && (
          <LoaderWrapper>
            <DotsLoader />
          </LoaderWrapper>
        )}
        <IconWrapper>
          <IconUpload />
        </IconWrapper>
        <ContentWrapper>
          <Text4 $variant="bold">
            {isTouch ? (
              <FormattedMessage id="comp.image_picker.hint" defaultMessage="Click to upload" />
            ) : (
              <FormattedMessage
                id="comp.image_picker.hint_dnd"
                defaultMessage="Click to upload or drag and drop"
              />
            )}
          </Text4>
          <Text5>
            {mimeTypes?.map(formatMimeType).join(', ').toUpperCase()}
            {(!!minWidth || !!minHeight) && (
              // eslint-disable-next-line formatjs/no-literal-string-in-jsx
              <>
                (
                <FormattedMessage
                  id="comp.image_picker.hint_dimensions"
                  defaultMessage="Minimum image size is {dimensions}px"
                  values={{ dimensions: `${minWidth}x${minHeight}` }}
                  // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                />
                )
              </>
            )}
          </Text5>
        </ContentWrapper>
      </Button>
      <Input
        data-testid="image-picker-input"
        {...props}
        accept={accept ?? 'image/*'}
        onChange={onChange}
        ref={inputRef}
        type="file"
      />
    </>
  );
};
